import React from 'react';
import {
  ActivityIndicator,
  Alert,
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';
import { COLORS } from '../constants/theme';
import { useAddFavourite, useIsFavourite, useRemoveFavourite } from '../hooks/useFavourites';
import { OwnershipScope } from '../types/favourites';
import { getFavouriteErrorMessage } from '../utils/favouriteErrors';

interface FavouriteToggleButtonProps {
  readonly isin: string;
  readonly ownershipScope?: OwnershipScope;
  readonly size?: 'sm' | 'md';
  readonly style?: ViewStyle;
}

const showFavouriteError = (message: string): void => {
  if (Platform.OS === 'web') {
    window.alert(`Error: ${message}`);
    return;
  }
  Alert.alert('Error', message);
};

export const FavouriteToggleButton: React.FC<FavouriteToggleButtonProps> = ({
  isin,
  ownershipScope = 'group',
  size = 'md',
  style,
}) => {
  const { isFavourite, isLoading: isListLoading, isFetched } = useIsFavourite(isin);
  const addFavourite = useAddFavourite();
  const removeFavourite = useRemoveFavourite();

  const isMutating = addFavourite.isPending || removeFavourite.isPending;
  const isResolvingState = isListLoading && !isFetched;

  const handlePress = async (): Promise<void> => {
    if (!isin || isMutating || isResolvingState) {
      return;
    }

    try {
      if (isFavourite) {
        await removeFavourite.mutateAsync(isin);
        return;
      }

      await addFavourite.mutateAsync({ isin, ownershipScope });
    } catch (error: unknown) {
      const message = getFavouriteErrorMessage(error);
      if (message) {
        showFavouriteError(message);
      }
    }
  };

  const iconSize = size === 'sm' ? 18 : 22;

  return (
    <TouchableOpacity
      style={[
        styles.button,
        size === 'sm' ? styles.buttonSm : styles.buttonMd,
        (isMutating || isResolvingState) && styles.buttonDisabled,
        style,
      ]}
      onPress={(event) => {
        if (
          event &&
          typeof (event as { stopPropagation?: () => void }).stopPropagation === 'function'
        ) {
          (event as { stopPropagation: () => void }).stopPropagation();
        }
        void handlePress();
      }}
      onPressIn={(event) => {
        if (
          event &&
          typeof (event as { stopPropagation?: () => void }).stopPropagation === 'function'
        ) {
          (event as { stopPropagation: () => void }).stopPropagation();
        }
      }}
      disabled={isMutating || isResolvingState || !isin}
      activeOpacity={0.7}
      accessibilityRole="button"
      accessibilityLabel={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
    >
      {isResolvingState ? (
        <ActivityIndicator size="small" color={COLORS.primary} />
      ) : (
        <Text
          style={[
            styles.icon,
            { fontSize: iconSize },
            isFavourite ? styles.iconActive : styles.iconInactive,
            isMutating && styles.iconMutating,
          ]}
        >
          {isFavourite ? '★' : '☆'}
        </Text>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  button: {
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        cursor: 'pointer',
      },
    }),
  },
  buttonMd: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: COLORS.white,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  buttonSm: {
    width: 28,
    height: 28,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  icon: {
    fontWeight: '600',
    lineHeight: 24,
  },
  iconActive: {
    color: COLORS.chartAccent,
  },
  iconInactive: {
    color: COLORS.textSecondary,
  },
  iconMutating: {
    opacity: 0.7,
  },
});
